/**
 * Chart theming.
 *
 * The analytics charts are built inside `public/legacy/core.js` with their
 * colours, fonts and grid values written inline — five configs, each with its
 * own hardcoded `#5c5854` tick colour and `rgba(249,115,22,…)` fill. That has
 * three consequences this module fixes without editing the monolith:
 *
 *   - every series is the brand orange, so a chart with two datasets encodes
 *     nothing by colour;
 *   - none of it follows the light theme, because the hexes are frozen;
 *   - the tick font is the browser default, next to a UI set in Inter.
 *
 * `new Chart(...)` resolves the global at call time, so replacing `window.Chart`
 * with a subclass before the first render is enough to normalise every config
 * on the way through. Series colours are then assigned from the validated
 * categorical palette in tokens.css, in slot order — never cycled, never
 * hand-picked at the call site.
 */

/** The colours the monolith authored, mapped to the palette slot each now
 *  takes. Keeping the author's identity choice — rather than repainting every
 *  chart in slot order — is what preserves "duration is the blue one" and
 *  keeps a muscle group the same colour in the radar chart and in its tag. */
const LEGACY_SERIES = {
  '#f97316': '--viz-1', // brand: volume, frequency, the radar body
  '#a3e635': '--viz-1', // full body
  '#60a5fa': '--viz-2', // back, and the duration series
  '#a78bfa': '--viz-3', // shoulders
  '#34d399': '--viz-4', // legs
  '#fbbf24': '--viz-5', // arms
  '#f87171': '--viz-6', // chest
  '#38bdf8': '--viz-7', // cardio
  '#f472b6': '--viz-8', // core
};

const token = (name) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

/** True on phone-width viewports, where an axis has room for far fewer ticks. */
const narrow = () => typeof matchMedia === 'function' && matchMedia('(max-width: 768px)').matches;

/** `--viz-N` for a zero-based series index, wrapping at the eighth slot. */
const slot = (i) => token(`--viz-${(i % 8) + 1}`);

/** Normalises `#rgb`, `#rrggbb`, `#rrggbbaa`, `rgb()` and `rgba()` to
 *  `{ hex, alpha }`, so a colour can be looked up however it was authored —
 *  the monolith writes the same blue as `#60a5fa` in one chart and
 *  `rgba(96,165,250,0.15)` in the next. */
function parse(value) {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  const hex = v.match(/^#([0-9a-f]{3,8})$/i);
  if (hex) {
    const h = hex[1];
    const full = h.length <= 4 ? [...h].map((c) => c + c).join('') : h;
    return {
      hex: `#${full.slice(0, 6).toLowerCase()}`,
      alpha: full.length === 8 ? parseInt(full.slice(6, 8), 16) / 255 : 1,
    };
  }
  const fn = v.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+))?/i);
  if (!fn) return null;
  const to2 = (n) => Math.round(Number(n)).toString(16).padStart(2, '0');
  return {
    hex: `#${to2(fn[1])}${to2(fn[2])}${to2(fn[3])}`,
    alpha: fn[4] === undefined ? 1 : parseFloat(fn[4]),
  };
}

function withAlpha(color, alpha) {
  const c = parse(color);
  if (!c) return color;
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(c.hex.slice(i, i + 2), 16));
  return alpha >= 1 ? c.hex : `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Re-points one authored colour at the palette, preserving its alpha.
 * Unrecognised colours fall back to the series' own slot rather than being
 * left as-is: an off-palette hex is exactly what this module exists to remove.
 */
function recolor(value, fallbackSlot) {
  const c = parse(value);
  const key = c && LEGACY_SERIES[c.hex];
  const target = key ? token(key) : fallbackSlot;
  return withAlpha(target, c ? c.alpha : 1);
}

const recolorEach = (values, fallbackSlot) =>
  values.map((v, i) => recolor(v, fallbackSlot ?? slot(i)));

function themeDatasets(datasets, chartType) {
  datasets.forEach((ds, i) => {
    const series = slot(i);
    const type = ds.type || chartType;
    // A radar's fill is an area fill in everything but name.
    const isArea = ds.fill === true || ds.fill === 'origin' || type === 'radar';

    ds.borderColor = Array.isArray(ds.borderColor)
      ? recolorEach(ds.borderColor)
      : recolor(ds.borderColor, series);
    // The authored opacity is kept — it is the one thing the call site knows
    // that this module does not — but clamped by what the mark is: an area
    // fill sits under its own line and must stay transparent enough to read
    // the gridlines through, while a bar *is* the mark and stays near-solid.
    const authored = parse(ds.backgroundColor)?.alpha;
    const fillAlpha = isArea
      ? Math.min(authored ?? 0.14, 0.18)
      : type === 'bar'
        ? Math.max(authored ?? 0.9, 0.85)
        : authored ?? 1;
    ds.backgroundColor = Array.isArray(ds.backgroundColor)
      ? recolorEach(ds.backgroundColor)
      : withAlpha(recolor(ds.backgroundColor, series), fillAlpha);
    if (ds.pointBackgroundColor) {
      ds.pointBackgroundColor = Array.isArray(ds.pointBackgroundColor)
        ? recolorEach(ds.pointBackgroundColor)
        : recolor(ds.pointBackgroundColor, series);
    }
    if (ds.pointBorderColor) ds.pointBorderColor = recolor(ds.pointBorderColor, series);

    // Mark spec: thin lines, restrained points, a gentle rather than a bouncy
    // curve, and rounded bar ends anchored to the baseline.
    if (ds.borderWidth === undefined || ds.borderWidth > 2) ds.borderWidth = 2;
    if (typeof ds.tension === 'number') ds.tension = 0.25;
    if ('pointRadius' in ds) ds.pointRadius = 0;
    ds.pointHoverRadius = 5;
    ds.pointHitRadius = 12;
    if (type === 'bar') {
      ds.borderRadius = 4;
      ds.borderSkipped = 'bottom';
    }

    // An area fill that fades to nothing reads as a measurement trace rather
    // than a solid block of colour, and it lets the gridlines stay visible
    // under the curve. Chart.js hands the scriptable callback a chart with no
    // chartArea on the very first layout pass, so fall back to the flat tint.
    if (isArea) {
      const flat = withAlpha(series, fillAlpha);
      ds.backgroundColor = (ctx) => {
        const { chart } = ctx;
        if (!chart?.chartArea) return flat;
        const { top, bottom } = chart.chartArea;
        const g = chart.ctx.createLinearGradient(0, top, 0, bottom);
        g.addColorStop(0, withAlpha(series, Math.min(0.34, fillAlpha * 2.4)));
        g.addColorStop(1, withAlpha(series, 0));
        return g;
      };
    }

    // The most recent value is the one the reader came for, so it gets the
    // only visible point on the line.
    if (type === 'line' && Array.isArray(ds.data) && !Array.isArray(ds.pointBackgroundColor)) {
      const last = ds.data.length - 1;
      ds.pointRadius = (ctx) => (ctx.dataIndex === last ? 3.5 : 0);
      ds.pointBackgroundColor = series;
      ds.pointBorderColor = token('--surface-default');
      ds.pointBorderWidth = 2;
    }
  });
}

function themeScales(scales) {
  Object.values(scales || {}).forEach((scale) => {
    if (!scale || typeof scale !== 'object') return;
    scale.border = { ...(scale.border || {}), display: false };
    scale.grid = { ...(scale.grid || {}), color: token('--chart-grid'), drawTicks: false };
    scale.ticks = {
      ...(scale.ticks || {}),
      color: token('--chart-tick'),
      padding: 8,
      font: { ...((scale.ticks || {}).font || {}), size: 11, weight: 500 },
      // Twelve week labels on a 390px screen come out rotated 45 degrees and
      // stacked on each other. A phone gets four; the axis is for orientation,
      // not for reading every value off.
      maxTicksLimit: narrow() ? 4 : 12,
      maxRotation: narrow() ? 0 : 45,
      autoSkip: true,
    };
    // Radar and polar charts carry their labels on an angle axis.
    if (scale.pointLabels) {
      scale.pointLabels = {
        ...scale.pointLabels,
        color: token('--text-secondary'),
        font: { size: 11, weight: 500 },
      };
      scale.angleLines = { ...(scale.angleLines || {}), color: token('--chart-grid') };
    }
  });
}

function themeConfig(config) {
  if (!config || typeof config !== 'object') return config;
  if (config.data?.datasets) themeDatasets(config.data.datasets, config.type);

  config.options = config.options || {};
  themeScales(config.options.scales);

  // Colour transitions are switched off deliberately. The area fills below are
  // CanvasGradient objects, and Chart.js's colour animator can only interpolate
  // parseable colour values — handed a gradient it throws inside the animation
  // tick, which leaves the chart half-constructed and the canvas unusable on
  // the next render. Nothing is lost: these charts do not re-colour in place.
  config.options.animations = { ...(config.options.animations || {}), colors: false };

  const plugins = (config.options.plugins = config.options.plugins || {});
  plugins.tooltip = {
    ...(plugins.tooltip || {}),
    backgroundColor: token('--chart-tooltip-bg'),
    titleColor: token('--text-primary'),
    bodyColor: token('--text-secondary'),
    borderColor: token('--chart-tooltip-border'),
    borderWidth: 1,
    cornerRadius: 6,
    padding: 10,
    displayColors: true,
    boxPadding: 4,
    titleFont: { weight: 600, size: 12 },
    bodyFont: { size: 12 },
  };
  // A legend is required as soon as colour carries identity; a single series
  // is named by the panel title instead.
  if (plugins.legend?.display !== false || (config.data?.datasets || []).length > 1) {
    plugins.legend = {
      ...(plugins.legend || {}),
      display: (config.data?.datasets || []).length > 1,
      position: 'bottom',
      labels: {
        color: token('--text-secondary'),
        boxWidth: 8,
        boxHeight: 8,
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 16,
        font: { size: 12 },
      },
    };
  }
  return config;
}

function applyDefaults(Chart) {
  const d = Chart.defaults;
  d.font.family = token('--font-mono') || 'ui-monospace, monospace';
  d.font.size = 11;
  d.font.weight = 500;
  d.color = token('--text-secondary');
  d.borderColor = token('--chart-grid');
  d.animation = { duration: 220, easing: 'easeOutQuart' };
  d.maintainAspectRatio = false;
  d.elements.line.borderJoinStyle = 'round';
  d.elements.line.capBezierPoints = true;
  d.elements.point.hoverBorderWidth = 2;
  d.plugins.tooltip.intersect = false;
  d.plugins.tooltip.mode = 'index';
}

export function installCharts() {
  const Base = globalThis.Chart;
  if (typeof Base !== 'function') {
    console.warn('[ironpact] Chart.js is not loaded; charts keep their inline styling');
    return;
  }

  applyDefaults(Base);

  class ThemedChart extends Base {
    constructor(item, config) {
      // The monolith keeps its own `AppState.chartInstances` map and does not
      // always destroy the previous chart before re-rendering a view, which
      // throws "Canvas is already in use" and leaves the panel blank on the
      // second visit. Reclaiming the canvas here fixes it for every caller.
      try {
        const canvas = typeof item === 'string' ? document.getElementById(item) : item;
        const existing = Base.getChart?.(canvas?.canvas || canvas);
        if (existing) existing.destroy();
      } catch {
        /* fall through — a failed reclaim must not stop the chart rendering */
      }

      try {
        config = themeConfig(config);
      } catch (err) {
        console.error('[ironpact] chart theming failed, using the original config', err);
      }
      super(item, config);
    }
  }
  // Static members (defaults, register, getChart, …) resolve through the
  // prototype chain, so callers of `Chart.defaults` are unaffected.
  globalThis.Chart = ThemedChart;

  // Re-theme live charts when the appearance changes, rather than leaving dark
  // ticks on a light panel until the next navigation.
  new MutationObserver(() => {
    applyDefaults(Base);
    Object.values(Base.instances || {}).forEach((chart) => {
      try {
        themeConfig(chart.config);
        chart.update('none');
      } catch (err) {
        console.error('[ironpact] chart re-theme failed', err);
      }
    });
  }).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
}
